import { ActionIcon, Box, Card, Divider, Group, Text } from '@mantine/core';
import { IconCornerUpLeft, IconX } from '@tabler/icons-react';
import type { Editor } from '@tiptap/react';
import { iconProps } from 'components/icon-props';
import { RichTextArea } from 'components/RichTextArea';
import { i18nKeys, useTranslation } from 'locales';
import { type Mail, useLoadMail } from 'modules/Mails/hooks';
import { MailComposerActions } from 'modules/Mails/pages/MailDetail/components/MailComposerActions';
import { MailComposerAiPrompt } from 'modules/Mails/pages/MailDetail/components/MailComposerAiPrompt';
import { MailComposerRecipients } from 'modules/Mails/pages/MailDetail/components/MailComposerRecipients';
import { useDeleteDraft } from 'modules/Mails/pages/MailDetail/hooks/use-delete-draft';
import { useSaveDraft } from 'modules/Mails/pages/MailDetail/hooks/use-save-draft';
import { DebtorFileInsert } from 'modules/Mails/pages/NewMail/components/DebtorFileInsert';
import { SendEmailFormProvider, useNewMailForm } from 'modules/Mails/pages/NewMail/hooks';
import posthog from 'posthog-js';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { captureEvent } from 'shared/analytics/capture-event';
import { insertGeneratedReply } from '../utils/insert-generated-reply';

type MailDetailComposerProps = {
  closeReply: () => void;
  draftMail: Mail | null;
  userSignature: string;
  focusAiPromptSignal?: number;
};

export const MailDetailComposer = ({
  closeReply,
  draftMail,
  userSignature,
  focusAiPromptSignal,
}: MailDetailComposerProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const editorRef = useRef<Editor | null>(null);
  const [isAiAssisted, setIsAiAssisted] = useState(false);

  const { thread, refetchThread } = useLoadMail(id);
  const { deleteDraft } = useDeleteDraft();
  const { form, handleSend, isSendEmailLoading, setAttachments, initialBody } = useNewMailForm({
    source: 'reply',
    isAiAssisted,
    threadId: id,
    draftMail,
    onSuccess: () => {
      refetchThread();
      closeReply();
    },
  });

  const handleClose = () => {
    if (draftMail)
      deleteDraft(
        { draftId: draftMail.id, threadId: id },
        {
          onSuccess: () => {
            captureEvent(posthog, 'mail:draft_discarded', { was_ai_assisted: isAiAssisted });
            closeReply();
          },
        },
      );
    else closeReply();
  };

  const { isSavedAsDraft, isSaveDraftLoading } = useSaveDraft({
    source: 'reply',
    threadId: id,
    draftMailId: draftMail?.id ?? null,
    formValues: form.values,
    isSending: isSendEmailLoading,
    onDraftMissing: refetchThread,
  });

  const handleGenerated = (html: string) => {
    setIsAiAssisted(true);
    if (editorRef.current) insertGeneratedReply(editorRef.current, html);
  };

  const debtor = thread.debtor;

  const initialContent = initialBody ?? (userSignature ? `<br/><br/>${userSignature}` : '');

  return (
    <Card
      withBorder
      shadow="sm"
      padding={0}
      radius="md"
      mx="space8"
      mt="space6"
      mb="space8"
      id="reply-to-mail"
    >
      <SendEmailFormProvider form={form}>
        <form onSubmit={handleSend}>
          <Group p="space3" gap="space2" justify="space-between">
            <Group gap="space2">
              <IconCornerUpLeft {...iconProps(14)} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {t(i18nKeys.REPLY)}
              </Text>
            </Group>
            <ActionIcon
              type="button"
              variant="subtle"
              color="gray"
              size="sm"
              onClick={handleClose}
              aria-label={t(i18nKeys.EXTERNAL_MAIL.DETAIL_NEXT.COMPOSER_CLOSE)}
            >
              <IconX {...iconProps(14)} />
            </ActionIcon>
          </Group>
          <Divider />

          <MailComposerRecipients />
          <Divider />

          <RichTextArea
            content={initialContent}
            onChange={value => form.setFieldValue('body', value)}
            onFileChange={setAttachments}
            onEditorReady={editor => {
              editorRef.current = editor;
            }}
            styles={RICH_TEXT_STYLES}
            toolbarActions={
              debtor && <DebtorFileInsert debtorId={debtor.id} editorRef={editorRef} />
            }
          />

          <Divider />
          <Group p="space3" gap="space2" justify="space-between">
            <Box>
              <MailComposerAiPrompt
                threadId={id ?? ''}
                onGenerated={handleGenerated}
                focusSignal={focusAiPromptSignal}
              />
            </Box>
            <MailComposerActions
              onClose={handleClose}
              isSaveDraftLoading={isSaveDraftLoading}
              isSavedAsDraft={isSavedAsDraft}
              isSendEmailLoading={isSendEmailLoading}
            />
          </Group>
        </form>
      </SendEmailFormProvider>
    </Card>
  );
};

const RICH_TEXT_STYLES = {
  root: { borderRadius: 0, border: 'none' },
  toolbar: { borderRadius: 0 },
  content: {
    borderRadius: 0,
    minHeight: 220,
    '& .ProseMirror': { minHeight: 220 },
  },
};
